import dropRight from 'lodash/fp/dropRight';
import { type FunctionComponent, type ReactElement, useState } from 'react';
import { UserSettings } from 'components/management/UserSettings.types.ts';
import GAgGridPresets from 'components/technical/grids/GAgGridPresets.tsx';
import { useDefaultErrorHandling } from 'components/technical/UseDefaultErrorHandling';
import type { TupleKeyMap } from 'components/TupleKeyMap.ts';
import { useReportAssetGroup } from 'components/UseReportAssetGroups.tsx';
import { Box, Stack, useTheme } from '@mui/joy';
import { useMemo, useRef } from 'react';
import { bignumber } from 'mathjs';
import type { NotVerifiedAssetWithId } from 'components/market/asset/AssetService.tsx';
import { parseUtcDate } from 'components/date.utils.ts';

import { getDefaultPresets } from './defaultPresets.ts';
import type { RowData } from './Report.types';
import { mapSubAccountAndDimensionToSubFund } from './Report.utils.tsx';
import { type IAssetType, type IPortfolioReportQuery, usePortfolioReportQuery } from '../../../generated/graphql';
import { dateReadableValueGetter } from 'components/technical/grids/agGrid.utils.tsx';
import type { ValueGetterParams, GridApi, ColDef } from 'ag-grid-community';
import {
  accountColumn,
  amountColumn,
  assetTypeColumn,
  assetWithSymbolColumn,
  balanceColumn,
  clusterColumn,
  exposureColumn,
  navPercentageColumn,
  navAggs,
  protocolPositionColumn,
  sideWithBadgeColumnBasedOnSlotAndAmount,
  slotColumn,
  subAccountColumn,
  subFundGroupColumn,
  underlyingAssetColumn,
  venueColumn,
} from 'components/technical/grids/SharedReportColumns';
import type { Dayjs } from 'dayjs';
import { getDefaultRange } from '../../predefinedDateRanges.ts';
import PredefinedDateRangeButtonsWithCalendar from '../../technical/inputs/date/PredefinedDateRangeButtonsWithCalendar.tsx';
import { usePortfolioFilters } from '../../technical/PortfolioFilterDrawer/UsePortfolioFilters.tsx';
import { convertDateRangeToSinceToDate } from '../../dateRange.utils.ts';
import { timeAvgOfSumsFactory } from './agg.utils.ts';
import { useFinalColorScheme } from 'technical/useFinalColorScheme.ts';

type ReportProps = {
  data: IPortfolioReportQuery;
  idToSubAccount: Record<
    string,
    {
      id: string;
      name: string;
      account: {
        id: string;
        name: string;
        venue: {
          label: string;
        };
      };
    }
  >;
  assetClusters: string[];
  assetAndGroupClusterMapToGroup: TupleKeyMap<[string, string], string>;
};

const GROUP_COLUMN_MIN_WIDTH = 220;
const dateColId = 'date';
const customAgg = {
  timeAvgOfSums: timeAvgOfSumsFactory(dateColId),
  ...navAggs,
};

const Report: FunctionComponent<ReportProps> = ({
  data,
  assetClusters,
  assetAndGroupClusterMapToGroup,
  idToSubAccount,
}) => {
  const theme = useTheme();
  const scheme = useFinalColorScheme();
  const gridApi = useRef<GridApi | null>(null);

  const subFunds = data.portfolio.subFunds.list;
  const { subAccountAndDimensionToSubFund, subFundDimensions } = mapSubAccountAndDimensionToSubFund(subFunds);

  // remove the latest snapshot to use only daily snapshots
  const rows: RowData[] = dropRight(1, data.portfolio.snapshot).flatMap((snapshot) =>
    snapshot.positions.map((pos) => {
      return {
        ...pos,
        asset: pos.asset as NotVerifiedAssetWithId,
        date: snapshot.date,
        subAccount: idToSubAccount[pos.subAccount.id],
      };
    })
  );

  const columnDefs = useMemo(() => {
    // Precompute total NAV per date for better performance
    const totalNAVByDate = new Map<string, number>();

    for (const row of rows) {
      const dateKey = parseUtcDate(row.date).toISOString();
      let balance = 0;

      if (row.spot) {
        balance = bignumber(row.spot.balance).toNumber();
      } else if (row.derivative) {
        balance = bignumber(row.derivative.balance ?? 0).toNumber();
      }

      const currentTotal = totalNAVByDate.get(dateKey) ?? 0;
      totalNAVByDate.set(dateKey, currentTotal + balance);
    }

    const getTotalNAVForDate = (params: ValueGetterParams<RowData>): number => {
      if (!params.data?.date) {
        return 0;
      }

      return totalNAVByDate.get(parseUtcDate(params.data.date).toISOString()) ?? 0;
    };

    return [
      {
        colId: dateColId,
        headerName: 'Date',
        type: 'dateColumn',
        valueGetter: (params: ValueGetterParams<RowData>): string | undefined =>
          params.data ? dateReadableValueGetter(params.data.date) : undefined,
      },
      assetWithSymbolColumn<RowData>({ initialHide: false }),
      underlyingAssetColumn<RowData>({ initialHide: true }),
      ...assetClusters.map((cluster) => clusterColumn<RowData>(cluster, assetAndGroupClusterMapToGroup)),
      sideWithBadgeColumnBasedOnSlotAndAmount<RowData>(),
      assetTypeColumn<RowData & { asset: { type: IAssetType } }>(),
      slotColumn<RowData>(),
      protocolPositionColumn(),
      {
        headerName: 'Account Details',
        colId: 'account-details',
        marryChildren: true,
        children: [accountColumn<RowData>(), subAccountColumn<RowData>(), venueColumn<RowData>()],
      },
      {
        headerName: 'Sub-funds',
        colId: 'sub-funds',
        marryChildren: true,
        children: subFundDimensions.map((subFundDimension) =>
          subFundGroupColumn<RowData>(subFundDimension, subAccountAndDimensionToSubFund)
        ),
      },
      {
        headerName: 'Positions',
        colId: 'positions',
        marryChildren: true,
        children: [
          exposureColumn<RowData>({ sideAware: true, initialHide: true }),
          exposureColumn<RowData>({ sideAware: false, initialHide: true }),
          navPercentageColumn<RowData>({
            scheme,
            showBar: true,
            totalNAV: getTotalNAVForDate,
            theme,
            minNavValue: -1,
            maxNavValue: 1,
          }),
          balanceColumn<RowData>(),
          amountColumn<RowData>({ initialHide: true }),
        ],
      },
    ];
  }, [
    assetClusters,
    assetAndGroupClusterMapToGroup,
    subAccountAndDimensionToSubFund,
    subFundDimensions,
    theme,
    rows,
    scheme,
  ]);

  return (
    <GAgGridPresets<RowData>
      aggFuncs={customAgg}
      presetSettingsKey={UserSettings.PortfolioReportPresets}
      defaultPresets={getDefaultPresets()}
      enableCharts
      cellSelection
      rowData={rows}
      autoGroupColumnDef={{
        minWidth: GROUP_COLUMN_MIN_WIDTH,
      }}
      onGridReady={(params: { api: GridApi }) => {
        gridApi.current = params.api;
      }}
      onGridPreDestroyed={() => {
        gridApi.current = null;
      }}
      defaultColDef={{
        resizable: true,
        sortable: true,
        filter: true,
      }}
      sideBar={{
        toolPanels: ['columns', 'filters'],
        defaultToolPanel: 'columns',
      }}
      columnDefs={columnDefs as ColDef<RowData>[]}
    />
  );
};

const ReportWithDateRange = (): ReactElement => {
  const defaultDateRange = getDefaultRange();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(defaultDateRange.value);

  return (
    <Stack spacing={1.5} height="100%">
      <Box display="flex" justifyContent="space-between" alignSelf="flex-end">
        <PredefinedDateRangeButtonsWithCalendar
          alignRight={false}
          defaultValue={defaultDateRange}
          onChange={setDateRange}
        />
      </Box>
      <ReportContainer dateRange={dateRange} />
    </Stack>
  );
};

type ReportContainerProps = {
  dateRange: [Dayjs, Dayjs] | null;
};

const ReportContainer: FunctionComponent<ReportContainerProps> = ({ dateRange }): ReactElement => {
  const { portfolioFilters } = usePortfolioFilters();
  const portfolioReportQueryResults = useDefaultErrorHandling(
    usePortfolioReportQuery({
      variables: {
        ...convertDateRangeToSinceToDate(dateRange),
        subAccountAssetFilters: portfolioFilters,
      },
    })
  );

  const reportAssetGroup = useReportAssetGroup();

  if (!reportAssetGroup.loaded) {
    return <reportAssetGroup.Fallback />;
  }

  if (!portfolioReportQueryResults.loaded) {
    return <portfolioReportQueryResults.Fallback />;
  }

  const idToSubAccount = Object.fromEntries(
    portfolioReportQueryResults.data.portfolio.accounts.flatMap((acc) =>
      acc.subAccounts.map((sub) => [
        sub.id,
        {
          ...sub,
          account: acc,
        },
      ])
    )
  );

  return (
    <Report
      data={portfolioReportQueryResults.data}
      idToSubAccount={idToSubAccount}
      assetClusters={reportAssetGroup.clusters}
      assetAndGroupClusterMapToGroup={reportAssetGroup.assetAndGroupClusterMapToGroup}
    />
  );
};

export default ReportWithDateRange;
